import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, rateFor, costAccountsFor } from '../../api/accounts';

export default function VisaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAgent, agentRefId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isView = !!id;

  const [visa, setVisa] = useState(null);
  const [clientLabel, setClientLabel] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [commTiers, setCommTiers] = useState([]);
  const [commBlocks, setCommBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState('');
  const [form, setForm] = useState({
    client_id: '', visa_type_id: '', supplier_id: '',
    application_date: new Date().toISOString().slice(0, 10),
    submission_date: '', approval_date: '', expiry_date: '',
    cost_price: '', cost_currency_id: '', sell_price: '', sell_currency_id: '',
    exchange_rate: '1', visa_status: 'PENDING', payment_status: 'UNPAID', notes: '',
    care_of_agent_id: '', care_of_agent_label: '',
    agent_ref_id: '', agent_ref_label: '', agent_commission: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    supabase.from('suppliers').select('id, name').eq('supplier_type', 'Visa Agent').eq('is_active', true).order('name').then(({ data }) => setSuppliers(data || []));
    supabase.from('visa_types').select('id, name, country').eq('is_active', true).order('country').then(({ data }) => setVisaTypes(data || []));
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code').then(({ data }) => {
      setCurrencies(data || []);
      if (!id) { const lkr = (data || []).find(c => c.code === 'LKR'); if (lkr) { set('cost_currency_id', lkr.id); set('sell_currency_id', lkr.id); } }
    });
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => {
      const mapped = (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
      setAgents(mapped);
      if (isAgent && agentRefId && !id) {
        const me = mapped.find(a => a.id === agentRefId);
        if (me) setForm(f => ({ ...f, care_of_agent_id: me.id, care_of_agent_label: me.label, agent_ref_id: me.id, agent_ref_label: me.label }));
      }
    });
    supabase.from('commission_tables').select('*').eq('product_type', 'Visa').eq('is_active', true).order('gp_from').then(({ data }) => {
      setCommTiers(data || []);
      const blocks = [...new Set((data || []).map(t => t.rule_name).filter(Boolean))];
      setCommBlocks(blocks);
    });
    if (id) supabase.from('visas').select('*, clients(full_name), visa_types(name, country), suppliers(name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => setVisa(data));
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const gp = parseFloat(form.sell_price || 0) - parseFloat(form.cost_price || 0);
  const gpPct = parseFloat(form.sell_price || 0) > 0 ? ((gp / parseFloat(form.sell_price)) * 100).toFixed(1) : 0;
  const netGp = gp - parseFloat(form.agent_commission || 0);
  const netGpPct = parseFloat(form.sell_price || 0) > 0 ? ((netGp / parseFloat(form.sell_price)) * 100).toFixed(1) : 0;

  const calcAutoComm = (sellPrice, costPrice, agentId, blockOverride) => {
    const block = blockOverride !== undefined ? blockOverride : selectedBlock;
    const activeTiers = block ? commTiers.filter(t => t.rule_name === block) : commTiers;
    if (!agentId || !activeTiers.length) return '';
    const sell = parseFloat(sellPrice || 0);
    const cost = parseFloat(costPrice || 0);
    if (sell <= 0) return '';
    const gpAmt = sell - cost;
    const gpPctVal = (gpAmt / sell) * 100;
    const tier = activeTiers.slice().sort((a, b) => parseFloat(b.gp_from) - parseFloat(a.gp_from))
      .find(t => {
        const cmp = t.commission_type === 'PERCENT' ? gpPctVal : gpAmt;
        return cmp >= parseFloat(t.gp_from) && (t.gp_to == null || cmp < parseFloat(t.gp_to));
      });
    if (!tier) return '';
    if (tier.fixed_amount != null && parseFloat(tier.fixed_amount) > 0) return String(parseFloat(tier.fixed_amount).toFixed(2));
    const basis = tier.commission_basis || 'ON_GP';
    const base = basis === 'ON_GP' ? gpAmt : basis === 'ON_SELL' ? sell : cost;
    return String((base * parseFloat(tier.commission_percent || 0) / 100).toFixed(2));
  };

  const setWithAutoComm = (k, v) => {
    setForm(f => {
      const updated = { ...f, [k]: v };
      if (['sell_price', 'cost_price', 'agent_ref_id'].includes(k)) {
        updated.agent_commission = calcAutoComm(updated.sell_price, updated.cost_price, updated.agent_ref_id);
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!form.client_id) return setError('Client required');
    setSaving(true); setError('');
    try {
      const visa_ref = 'VZ' + Date.now().toString().slice(-7);
      const { data, error: err } = await supabase.from('visas').insert({
        visa_ref,
        client_id: form.client_id,
        visa_type_id: form.visa_type_id || null,
        supplier_id: form.supplier_id || null,
        application_date: form.application_date || null,
        submission_date: form.submission_date || null,
        approval_date: form.approval_date || null,
        expiry_date: form.expiry_date || null,
        cost_price: parseFloat(form.cost_price) || null,
        cost_currency_id: form.cost_currency_id || null,
        sell_price: parseFloat(form.sell_price) || null,
        sell_currency_id: form.sell_currency_id || null,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        gross_profit: gp || null,
        gp_margin_percent: parseFloat(form.sell_price) > 0 ? ((gp / parseFloat(form.sell_price)) * 100) : null,
        care_of_agent_id: form.care_of_agent_id || null,
        visa_status: form.visa_status,
        payment_status: form.payment_status,
        notes: form.notes,
        created_by: username,
      }).select().single();
      if (err) throw err;

      const sellCode = currencies.find(c => c.id === form.sell_currency_id)?.code;
      const costCode = currencies.find(c => c.id === form.cost_currency_id)?.code;
      const jdate = form.application_date || new Date().toISOString().slice(0, 10);

      // Cost journal (cost currency): DR Visa Government Fee Cost / CR Visa Fees Payable
      if (parseFloat(form.cost_price) > 0) {
        const { expenseAccount, payableAccount } = costAccountsFor('Visa', costCode);
        await postJournal({
          date: jdate,
          description: `Visa cost: ${visa_ref}`,
          reference_type: 'VISA_COST',
          reference_id: data.id,
          currency_id: form.cost_currency_id || null,
          exchange_rate: rateFor(costCode, form.exchange_rate),
          lines: [
            { account_id: expenseAccount, debit_amount: parseFloat(form.cost_price), credit_amount: 0,                          description: `Cost - ${visa_ref}` },
            { account_id: payableAccount, debit_amount: 0,                          credit_amount: parseFloat(form.cost_price), description: `Payable - ${visa_ref}`, party_type: form.supplier_id ? 'supplier' : null, party_id: form.supplier_id || null },
          ],
          created_by: username,
        });
      }

      // Commission: recognise expense at sale (matching principle), track payment status separately
      if (form.agent_ref_id && form.agent_commission) {
        const commAmt = parseFloat(form.agent_commission);
        if (commAmt > 0) {
          await postJournal({
            date: new Date().toISOString().slice(0, 10),
            description: `Agent commission: Visa ${data.id?.slice(0, 8)}`,
            reference_type: 'VISA_COMMISSION',
            reference_id: data.id,
            exchange_rate: 1,
            lines: [
              { account_id: ACCOUNTS.COMMISSION_EXPENSE, debit_amount: commAmt, credit_amount: 0, description: 'Commission expense' },
              { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: 0, credit_amount: commAmt, description: 'Commission payable', party_type: 'agent', party_id: form.agent_ref_id },
            ],
            created_by: username,
          });
          await supabase.from('commissions').insert({
            agent_ref_id: form.agent_ref_id,
            reference_type: 'VISA',
            reference_id: data.id,
            product_type: 'Visa',
            gp_amount: gp || 0,
            commission_amount: commAmt,
            commission_basis: 'FIXED_AMOUNT',
            commission_status: 'PENDING',
          });
        }
      }
      navigate(`/visas/${data.id}`);
    } catch (err) { setError(err.message); setSaving(false); }
  };

  const updateStatus = async (visa_status) => {
    await supabase.from('visas').update({ visa_status, updated_by: username, updated_at: new Date().toISOString() }).eq('id', id);
    // Reverse all journals on cancellation (sale, cost, commission)
    if (visa_status === 'CANCELLED') {
      const { data: jes } = await supabase.from('journal_entries').select('id')
        .in('reference_type', ['VISA', 'VISA_COST', 'VISA_COMMISSION'])
        .eq('reference_id', id).eq('status', 'POSTED').eq('is_reversed', false);
      for (const je of jes || []) await voidJournal(je.id, 'Visa cancelled', username);
    }
    supabase.from('visas').select('*, clients(full_name), visa_types(name, country), suppliers(name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code)').eq('id', id).single().then(({ data }) => setVisa(data));
  };

  const openVisaEdit = () => {
    setEditForm({
      cost_price: String(visa.cost_price || ''),
      cost_currency_id: visa.cost_currency_id || '',
      sell_price: String(visa.sell_price || ''),
      sell_currency_id: visa.sell_currency_id || '',
      exchange_rate: String(visa.exchange_rate || '1'),
      payment_status: visa.payment_status || 'UNPAID',
      submission_date: visa.submission_date?.slice(0, 10) || '',
      approval_date: visa.approval_date?.slice(0, 10) || '',
      expiry_date: visa.expiry_date?.slice(0, 10) || '',
      notes: visa.notes || '',
    });
    setEditing(true);
  };

  const setEV = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleSaveVisaEdit = async () => {
    setEditSaving(true);
    const sell = parseFloat(editForm.sell_price) || 0;
    const cost = parseFloat(editForm.cost_price) || 0;
    const gpVal = sell - cost;
    await supabase.from('visas').update({
      cost_price: cost || null,
      cost_currency_id: editForm.cost_currency_id || null,
      sell_price: sell || null,
      sell_currency_id: editForm.sell_currency_id || null,
      exchange_rate: parseFloat(editForm.exchange_rate) || 1,
      payment_status: editForm.payment_status,
      submission_date: editForm.submission_date || null,
      approval_date: editForm.approval_date || null,
      expiry_date: editForm.expiry_date || null,
      notes: editForm.notes,
      gross_profit: gpVal,
      updated_by: username,
      updated_at: new Date().toISOString(),
    }).eq('id', id);
    const { data } = await supabase.from('visas').select('*, clients(full_name), visa_types(name, country), suppliers(name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single();
    setVisa(data);
    setEditing(false);
    setEditSaving(false);
  };

  if (isView) {
    if (!visa) return <div className="loading-screen"><div className="spinner" /></div>;
    return (
      <div>
        <div className="page-header">
          <div><div className="page-title">{visa.visa_ref}</div><div className="page-subtitle">{visa.clients?.full_name}  {visa.visa_types?.name} ({visa.visa_types?.country})</div></div>
          <div style={{ display: 'flex', gap: 8 }}>
            {visa.visa_status !== 'CANCELLED' && (
              <button className="btn btn-secondary btn-sm" onClick={openVisaEdit}>Edit</button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={async () => {
              const { data: existing } = await supabase.from('invoices').select('id').eq('source_id', visa.id).eq('invoice_type', 'INVOICE').neq('status', 'VOID').limit(1);
              if (existing && existing.length > 0) { navigate(`/invoices/${existing[0].id}`); return; }
              const yy = String(new Date().getFullYear()).slice(-2);
              const { data: maxRow } = await supabase.from('invoices').select('invoice_number').ilike('invoice_number', `TLS-${yy}-%`).order('invoice_number', { ascending: false }).limit(1);
              const lastSeq = maxRow?.[0]?.invoice_number ? parseInt(maxRow[0].invoice_number.split('-').pop()) : 0;
              const num = `TLS-${yy}-${String(lastSeq + 1).padStart(4, '0')}`;
              const sell = parseFloat(visa.sell_price || 0);
              const country = visa.visa_types?.country || '';
              const vtype = visa.visa_types?.name || '';
              const lineItems = [{ description: `Visa Processing${country ? ` — ${country}` : ''}${vtype ? ` (${vtype})` : ''}`, sub: null, qty: 1, unitPrice: sell, amount: sell }];
              const today = new Date().toISOString().slice(0, 10);
              const { data: inv } = await supabase.from('invoices').insert({
                invoice_number: num, invoice_type: 'INVOICE', client_id: visa.client_id,
                source_type: 'visa', source_id: visa.id, line_items: lineItems,
                issued_date: today, status: 'DRAFT', created_by: username,
              }).select().single();
              if (sell > 0) {
                const je = await postJournal({
                  date: today, description: `Invoice ${num}`, reference_type: 'INVOICE_VISA', reference_id: inv.id,
                  currency_id: visa.sell_currency_id || null, exchange_rate: rateFor(visa.sell_currency?.code, visa.exchange_rate || 1),
                  lines: [
                    { account_id: ACCOUNTS.AR, debit_amount: sell, credit_amount: 0, description: `AR - ${num}`, party_type: 'client', party_id: visa.client_id },
                    { account_id: ACCOUNTS.VISA_REVENUE, debit_amount: 0, credit_amount: sell, description: `Revenue - ${num}` },
                  ],
                  created_by: username,
                });
                if (je?.id) await supabase.from('invoices').update({ journal_entry_id: je.id }).eq('id', inv.id);
              }
              await supabase.from('visas').update({ invoice_id: inv.id }).eq('id', visa.id);
              navigate(`/invoices/${inv.id}`);
            }}>Generate Invoice</button>
            {visa.visa_status === 'PENDING' && <button className="btn btn-secondary btn-sm" onClick={() => updateStatus('APPLIED')}>Mark Applied</button>}
            {visa.visa_status === 'APPLIED' && <button className="btn btn-primary btn-sm" onClick={() => updateStatus('APPROVED')}>Mark Approved</button>}
            {visa.visa_status === 'APPLIED' && <button className="btn btn-secondary btn-sm" onClick={() => updateStatus('REJECTED')}>Mark Rejected</button>}
            {!['CANCELLED'].includes(visa.visa_status) && <button className="btn btn-secondary btn-sm" onClick={() => updateStatus('CANCELLED')}>Cancel</button>}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            {[['Client', visa.clients?.full_name], ['Visa Type', visa.visa_types?.name], ['Country', visa.visa_types?.country], ['Supplier', visa.suppliers?.name], ['Applied', visa.application_date?.slice(0, 10)], ['Submitted', visa.submission_date?.slice(0, 10)], ['Approved', visa.approval_date?.slice(0, 10)], ['Expiry', visa.expiry_date?.slice(0, 10)], ['Status', visa.visa_status], ['Payment', visa.payment_status]].map(([l, v]) => v ? (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
          </div>
          <div className="card">
            {(isAgent
              ? [['Sell', `${visa.sell_currency?.code} ${parseFloat(visa.sell_price || 0).toLocaleString()}`], ['Created By', visa.created_by]]
              : [['Cost', `${visa.cost_currency?.code} ${parseFloat(visa.cost_price || 0).toLocaleString()}`], ['Sell', `${visa.sell_currency?.code} ${parseFloat(visa.sell_price || 0).toLocaleString()}`], ['GP', parseFloat(visa.gross_profit || 0).toLocaleString()], ['Created By', visa.created_by]]
            ).map(([l, v]) => v ? (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
            {visa.notes && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>{visa.notes}</div>}
          </div>
        </div>

        {editing && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: 520, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="card-title" style={{ marginBottom: 16 }}>Edit Visa — {visa.visa_ref}</div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Cost Price</label>
                  <input className="form-input" type="text" inputMode="decimal" value={editForm.cost_price} onChange={e => setEV('cost_price', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Cost Currency</label>
                  <TypeSearch options={currencies} value={currencies.find(c => c.id === editForm.cost_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => setEV('cost_currency_id', c.id)} onClear={() => setEV('cost_currency_id', '')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sell Price</label>
                  <input className="form-input" type="text" inputMode="decimal" value={editForm.sell_price} onChange={e => setEV('sell_price', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sell Currency</label>
                  <TypeSearch options={currencies} value={currencies.find(c => c.id === editForm.sell_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => setEV('sell_currency_id', c.id)} onClear={() => setEV('sell_currency_id', '')} />
                </div>
                <div className="form-group">
                  <label className="form-label">FX Rate</label>
                  <input className="form-input" type="text" inputMode="decimal" value={editForm.exchange_rate} onChange={e => setEV('exchange_rate', e.target.value)} placeholder="1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <TypeSearch options={['UNPAID','PARTIAL','PAID'].map(s => ({ id: s, label: s }))} value={editForm.payment_status} displayKey="label" placeholder="Status…" onSelect={s => setEV('payment_status', s.id)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Submission Date</label>
                  <input className="form-input" type="date" value={editForm.submission_date} onChange={e => setEV('submission_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Approval Date</label>
                  <input className="form-input" type="date" value={editForm.approval_date} onChange={e => setEV('approval_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <input className="form-input" type="date" value={editForm.expiry_date} onChange={e => setEV('expiry_date', e.target.value)} />
                </div>
                {editForm.sell_price && editForm.cost_price && (
                  <div className="form-group">
                    <label className="form-label">GP</label>
                    <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: '1px solid var(--border)', fontWeight: 700, color: (parseFloat(editForm.sell_price) - parseFloat(editForm.cost_price)) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {(parseFloat(editForm.sell_price) - parseFloat(editForm.cost_price)).toLocaleString()} ({parseFloat(editForm.sell_price) > 0 ? (((parseFloat(editForm.sell_price) - parseFloat(editForm.cost_price)) / parseFloat(editForm.sell_price)) * 100).toFixed(1) : 0}%)
                    </div>
                  </div>
                )}
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={3} value={editForm.notes} onChange={e => setEV('notes', e.target.value)} placeholder="Any notes…" style={{ resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEditing(false)} disabled={editSaving}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSaveVisaEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">New Visa Application</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Create Visa'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      <div className="card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Client *</label>
            <TypeSearch
              fetchFn={async (q) => { const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).order('full_name').limit(30); return data || []; }}
              value={clientLabel}
              displayKey="full_name"
              placeholder="Type client name…"
              onSelect={c => { setClientLabel(c.full_name); set('client_id', c.id); }}
              onClear={() => { setClientLabel(''); set('client_id', ''); }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Visa Type</label>
            <TypeSearch
              options={visaTypes.map(v => ({ ...v, label: `${v.country} — ${v.name}` }))}
              value={visaTypes.find(v => v.id === form.visa_type_id) ? `${visaTypes.find(v => v.id === form.visa_type_id).country} — ${visaTypes.find(v => v.id === form.visa_type_id).name}` : ''}
              displayKey="label"
              placeholder="Type country or visa type…"
              onSelect={v => set('visa_type_id', v.id)}
              onClear={() => set('visa_type_id', '')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Visa Agent / Supplier</label>
            <TypeSearch options={suppliers} value={suppliers.find(s => s.id === form.supplier_id)?.name || ''} displayKey="name" placeholder="Type supplier name…" onSelect={s => set('supplier_id', s.id)} onClear={() => set('supplier_id', '')} />
          </div>
          <div className="form-group"><label className="form-label">Application Date</label><input className="form-input" type="date" value={form.application_date} onChange={e => set('application_date', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Submission Date</label><input className="form-input" type="date" value={form.submission_date} onChange={e => set('submission_date', e.target.value)} /></div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <TypeSearch
              options={['PENDING','APPLIED','APPROVED','REJECTED','CANCELLED'].map(s => ({ id: s, label: s }))}
              value={form.visa_status}
              displayKey="label"
              placeholder="Status…"
              onSelect={s => set('visa_status', s.id)}
            />
          </div>
          {!isAgent && (
            <>
              <div className="form-group"><label className="form-label">Cost Price</label><input className="form-input" type="text" inputMode="decimal" value={form.cost_price} onChange={e => setWithAutoComm('cost_price', e.target.value)} placeholder="0.00" /></div>
              <div className="form-group">
                <label className="form-label">Cost Currency</label>
                <TypeSearch options={currencies} value={currencies.find(c => c.id === form.cost_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => set('cost_currency_id', c.id)} onClear={() => set('cost_currency_id', '')} />
              </div>
            </>
          )}
          <div className="form-group"><label className="form-label">Sell Price</label><input className="form-input" type="text" inputMode="decimal" value={form.sell_price} onChange={e => setWithAutoComm('sell_price', e.target.value)} placeholder="0.00" /></div>
          <div className="form-group">
            <label className="form-label">Sell Currency</label>
            <TypeSearch options={currencies} value={currencies.find(c => c.id === form.sell_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => set('sell_currency_id', c.id)} onClear={() => set('sell_currency_id', '')} />
          </div>
          {!isAgent && form.sell_price && form.cost_price && (
            <div className="form-group">
              <label className="form-label">GP</label>
              <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: '1px solid var(--border)', fontWeight: 700, color: gp >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {gp.toLocaleString()} ({gpPct}%)
              </div>
            </div>
          )}
          {!isAgent && form.sell_price && form.cost_price && form.agent_commission && (
            <div className="form-group">
              <label className="form-label">Net GP <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)' }}>after commission</span></label>
              <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: `1px solid ${netGp >= 0 ? 'var(--success)' : 'var(--danger)'}`, fontWeight: 700, color: netGp >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {netGp.toLocaleString()} ({netGpPct}%)
              </div>
            </div>
          )}
          {!isAgent && (
            <>
              <div className="form-group">
                <label className="form-label">Care of (Agent)</label>
                <TypeSearch
                  options={agents}
                  value={form.care_of_agent_label}
                  displayKey="label"
                  placeholder="TLS code or agent name…"
                  onSelect={a => { set('care_of_agent_id', a.id); set('care_of_agent_label', a.label); }}
                  onClear={() => { set('care_of_agent_id', ''); set('care_of_agent_label', ''); }}
                  renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>}
                />
              </div>
              {commBlocks.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Commission Block</label>
                  <select className="form-input" value={selectedBlock} onChange={e => {
                    const newBlock = e.target.value;
                    setSelectedBlock(newBlock);
                    setForm(f => ({ ...f, agent_commission: calcAutoComm(f.sell_price, f.cost_price, f.agent_ref_id, newBlock) }));
                  }}>
                    <option value="">— Select block —</option>
                    {commBlocks.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Agent Commission</label>
                <TypeSearch
                  options={agents}
                  value={form.agent_ref_label}
                  displayKey="label"
                  placeholder="TLS code or agent name…"
                  onSelect={a => { setWithAutoComm('agent_ref_id', a.id); set('agent_ref_label', a.label); }}
                  onClear={() => { set('agent_ref_id', ''); set('agent_ref_label', ''); set('agent_commission', ''); }}
                  renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Commission Amount (LKR) {form.agent_ref_id && form.agent_commission ? <span style={{ fontWeight: 400, color: 'var(--primary)', fontSize: 12 }}>— auto-calculated</span> : ''}</label>
                <input className="form-input" type="text" inputMode="decimal" value={form.agent_commission} onChange={e => set('agent_commission', e.target.value)} placeholder="0.00" disabled={!form.agent_ref_id} />
              </div>
            </>
          )}
          <div className="form-group full-width"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}
